import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Chip,
  Stack,
  Card,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../types';
import ImageGallery from './ImageGallery';
import { extractMediaFromSources } from '../utils/imageUtils';

interface ChatAreaProps {
  messages: ChatMessage[];
  loading: boolean;
  onSendMessage: (message: string) => void;
  sidebarOpen: boolean;
  sourcePanelOpen: boolean;
  drawerWidth: number;
  sourcePanelWidth: number;
}

// Full width chat area component with modern design
const ChatArea: React.FC<ChatAreaProps> = ({
  messages,
  loading,
  onSendMessage,
  sidebarOpen,
  sourcePanelOpen,
  drawerWidth,
  sourcePanelWidth,
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);
  const lastMessageCount = useRef(0);
  const isStreaming = useRef(false);

  // Check if user is near the bottom of the chat
  const isNearBottom = () => {
    const container = chatContainerRef.current;
    if (!container) return true;
    const threshold = 150; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  // Handle user scroll - detect if they scrolled up
  const handleScroll = () => {
    // Only update userScrolledUp if we're currently streaming
    // This prevents the flag from being reset during normal browsing
    if (isStreaming.current) {
      userScrolledUp.current = !isNearBottom();
    }
  };

  const scrollToBottom = (instant = false) => {
    if (userScrolledUp.current) return;
    
    const container = chatContainerRef.current;
    if (container) {
      // Use instant scroll during streaming for smoother experience
      container.scrollTo({
        top: container.scrollHeight,
        behavior: instant ? 'auto' : 'smooth'
      });
    }
  };

  // Handle scroll behavior based on message changes
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const messageCountChanged = messages.length !== lastMessageCount.current;
    lastMessageCount.current = messages.length;
    
    // New message added (user or AI starting)
    if (messageCountChanged) {
      if (lastMessage?.isUser) {
        // User sent a message - reset scroll state and scroll down
        userScrolledUp.current = false;
        isStreaming.current = false;
        scrollToBottom(false);
      } else if (lastMessage && !lastMessage.isUser) {
        // New AI message - scroll to show it
        userScrolledUp.current = false;
        isStreaming.current = lastMessage.isStreaming || false;
        scrollToBottom(false);
      }
    } else if (lastMessage?.isStreaming) {
      // Content update during streaming - use instant scroll if near bottom
      isStreaming.current = true;
      if (!userScrolledUp.current && isNearBottom()) {
        scrollToBottom(true); // instant scroll during streaming
      }
    } else if (lastMessage && !lastMessage.isStreaming && isStreaming.current) {
      // Streaming just finished
      isStreaming.current = false;
      if (!userScrolledUp.current) {
        scrollToBottom(false); // smooth scroll to final position
      }
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (inputMessage.trim()) {
      onSendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };



  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        backgroundColor: 'grey.50',
        overflow: 'hidden',
      }}
    >
      {/* Chat Messages Area - Full Width */}
      <Box
        ref={chatContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflow: 'auto',
          pt: 9, // Account for header
          backgroundColor: 'grey.50',
        }}
      >
        {messages.length === 0 ? (
          <Box sx={{ py: 3, px: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '4px solid',
                    borderColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src="/burngpt_logo.png"
                    alt="BurnGPT"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: '45% center',
                    }}
                  />
                </Box>
              </Box>
              
              <Typography
                variant="h4"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #00ACDD 0%, #0078A0 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome to BurnGPT
              </Typography>
              
              {/* Preview Disclaimer */}
              <Box
                sx={{
                  mb: 3,
                  p: 0,
                  maxWidth: '680px',
                  background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CD 100%)',
                  border: '1px solid #E6C200',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(230, 194, 0, 0.15)',
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFC000 100%)',
                    py: 1,
                    px: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: '#5D4E00',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontSize: '0.75rem',
                    }}
                  >
                    ⚠️ Preview Release Only
                  </Typography>
                </Box>
                
                {/* Content */}
                <Box sx={{ p: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#5D4E00',
                      lineHeight: 1.7,
                      mb: 2,
                    }}
                  >
                    BurnGPT is in <strong>PREVIEW RELEASE ONLY</strong>. This is an extended beta test of our new AI capabilities. 
                    While you are using this - you are one of our trusted testers to perfect this solution.
                  </Typography>
                  
                  {/* Report Section */}
                  <Box
                    sx={{
                      pt: 2,
                      borderTop: '1px dashed #E6C200',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#5D4E00',
                        lineHeight: 1.7,
                        textAlign: 'center',
                      }}
                    >
                      Any incorrect answers or poor responses should be reported via the support portal{' '}
                      <a 
                        href="https://support.burnbootcamp.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          color: '#0066CC', 
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        HERE
                      </a>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#5D4E00',
                        mt: 1,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      under ticket pathway{' '}
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: '#5D4E00',
                          color: '#FFF',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                        }}
                      >
                        Technology → BurnGPT
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  px: 3,
                  py: 2,
                  backgroundColor: 'white',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  display: 'inline-block',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>
                  Try asking me about
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {[
                    'Franchise requirements',
                    'Location performance tips',
                    'Marketing guidelines',
                    'Staff training resources',
                    'Operations procedures',
                    'Brand compliance',
                  ].map((suggestion) => (
                    <Chip
                      key={suggestion}
                      label={suggestion}
                      size="small"
                      onClick={() => onSendMessage(suggestion)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: 'primary.50',
                        color: 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.200',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0, 172, 221, 0.25)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 2, px: 3 }}>
            <Stack spacing={0}>
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      mb: 3,
                      alignItems: message.isUser ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {/* Message Content */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        maxWidth: '85%',
                        ...(message.isUser && { flexDirection: 'row-reverse' }),
                      }}
                    >
                      {/* Avatar */}
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: message.isUser ? 'secondary.main' : 'primary.main',
                          flexShrink: 0,
                        }}
                      >
                        {message.isUser ? <PersonIcon /> : <BotIcon />}
                      </Avatar>

                      {/* Message Bubble */}
                      <Box
                        sx={{
                          position: 'relative',
                          maxWidth: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: message.isUser ? 'flex-end' : 'flex-start',
                        }}
                      >
                        {/* Message Content */}
                        <Box
                          sx={{
                            px: 3,
                            py: 2,
                            borderRadius: '16px',
                            backgroundColor: message.isUser ? 'primary.main' : 'background.paper',
                            color: message.isUser ? 'primary.contrastText' : 'text.primary',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                            border: message.isUser ? 'none' : '1px solid',
                            borderColor: 'divider',
                            wordWrap: 'break-word',
                            minWidth: 'fit-content',
                            maxWidth: '100%',
                          }}
                        >
                          {message.isUser ? (
                            <Typography
                              variant="body1"
                              sx={{
                                lineHeight: 1.5,
                                color: 'inherit',
                              }}
                            >
                              {message.content}
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                '& p': { margin: 0, lineHeight: 1.6 },
                                '& p + p': { mt: 1 },
                                '& ul, & ol': { pl: 2, mt: 1, mb: 1 },
                                '& li': { mb: 0.5 },
                                '& strong': { fontWeight: 600 },
                                '& code': {
                                  backgroundColor: 'action.hover',
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  fontSize: '0.875em',
                                  fontFamily: 'monospace',
                                },
                                '& pre': {
                                  backgroundColor: 'action.hover',
                                  p: 1.5,
                                  borderRadius: 1,
                                  overflow: 'auto',
                                  mt: 1,
                                },
                                // Table styles for markdown tables
                                '& table': {
                                  borderCollapse: 'collapse',
                                  width: '100%',
                                  mt: 2,
                                  mb: 2,
                                  fontSize: 'inherit',  // Match parent font size
                                  fontFamily: 'inherit',  // Match parent font family
                                },
                                '& th, & td': {
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  px: 1.5,
                                  py: 1,
                                  textAlign: 'left',
                                  fontSize: 'inherit',  // Consistent font size
                                  fontFamily: 'inherit',  // Consistent font family
                                  lineHeight: 1.6,  // Match body line height
                                },
                                '& th': {
                                  backgroundColor: 'action.hover',
                                  fontWeight: 600,
                                },
                                '& tr:nth-of-type(even)': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',  // Subtle alternating rows
                                },
                                // Ensure links in tables match other links
                                '& table a': {
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                },
                              }}
                            >
                              {/* Show searching state or content */}
                              {message.isStreaming && !message.content ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 0.5,
                                      '& > span': {
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main',
                                        animation: 'bounce 1.4s infinite ease-in-out both',
                                      },
                                      '& > span:nth-of-type(1)': { animationDelay: '-0.32s' },
                                      '& > span:nth-of-type(2)': { animationDelay: '-0.16s' },
                                      '& > span:nth-of-type(3)': { animationDelay: '0s' },
                                      '@keyframes bounce': {
                                        '0%, 80%, 100%': { transform: 'scale(0)' },
                                        '40%': { transform: 'scale(1)' },
                                      },
                                    }}
                                  >
                                    <span />
                                    <span />
                                    <span />
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Searching knowledge base...
                                  </Typography>
                                </Box>
                              ) : (
                                <>
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                                  {/* Streaming cursor indicator */}
                                  {message.isStreaming && message.content && (
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'inline-block',
                                        width: '2px',
                                        height: '1em',
                                        backgroundColor: 'text.primary',
                                        animation: 'blink 0.8s infinite',
                                        ml: 0.25,
                                        verticalAlign: 'text-bottom',
                                        '@keyframes blink': {
                                          '0%, 50%': { opacity: 1 },
                                          '51%, 100%': { opacity: 0 },
                                        },
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </Box>
                          )}

                          {/* Media Gallery for AI messages with sources (images and PDFs) */}
                          {(() => {
                            if (message.isUser || !message.sources) {
                              return null;
                            }
                            const mediaItems = extractMediaFromSources(message.sources);
                            const show = mediaItems.length > 0;
                            // Debug logging to verify gallery conditions
                            // eslint-disable-next-line no-console
                            console.log('🖼️ MediaGallery Debug:', {
                              hasSources: !!message.sources,
                              numSources: message.sources?.length || 0,
                              extractedMedia: mediaItems.length,
                              mediaTypes: mediaItems.map(m => m.type),
                              firstMedia: mediaItems[0]?.url || 'none'
                            });
                            return show ? (
                              <Box sx={{ mt: 2 }}>
                                <ImageGallery 
                                  images={mediaItems}
                                  maxThumbnails={6}
                                />
                              </Box>
                            ) : null;
                          })()}
                        </Box>

                        {/* Timestamp below message */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.disabled',
                            fontSize: '0.75rem',
                            mt: 0.5,
                            px: 1,
                          }}
                        >
                          {format(message.timestamp, 'h:mm a')}
                        </Typography>
                      </Box>
                    </Box>


                  </Box>
                ))}
                
{/* Loading indicator removed - using streaming cursor instead */}
                
                <div ref={messagesEndRef} />
              </Stack>
          </Box>
        )}
      </Box>

      {/* Modern Input Area - Full Width */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderTop: '1px solid',
          borderColor: 'grey.200',
          p: 2,
        }}
      >
        <Box sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about your Burn Boot Camp franchise..."
                variant="outlined"
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused': {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 3px rgba(0, 172, 221, 0.1)',
                    },
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || loading}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0, 172, 221, 0.3)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatArea; 